import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Building2,
  User,
  Globe,
  Target,
  Sparkles,
  Rocket,
  Shield,
  Clock,
  Heart,
  Zap,
  Award,
  Phone,
  Mail,
  MapPin,
  Upload,
  CheckCircle,
  Star,
  Users,
  TrendingUp,
  Loader2,
  Home
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const STEPS = [
  { id: 1, title: 'Votre entreprise', icon: Building2 },
  { id: 2, title: 'Vos objectifs', icon: Target },
  { id: 3, title: 'Vos préférences', icon: Heart },
  { id: 4, title: 'Bienvenue !', icon: Rocket }
];

const BENEFITS = [
  { icon: Shield, title: 'Sécurité garantie', desc: 'Vos données sont protégées et confidentielles' },
  { icon: Clock, title: 'Gain de temps', desc: 'Un espace centralisé pour tous vos projets' },
  { icon: Zap, title: 'Réactivité', desc: 'Suivi en temps réel de l\'avancement' },
  { icon: Award, title: 'Qualité premium', desc: 'Des experts à votre service' }
];

const ClientOnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [themeSettings, setThemeSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_size: '',
    industry: '',
    website: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    main_objective: '',
    budget_range: '',
    project_timeline: '',
    interests: [],
    how_found_us: '',
    preferred_contact_method: 'email',
    newsletter: true
  });

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await api.get('/api/theme');
        setThemeSettings(res.data);
      } catch (e) {
        console.log('Theme not loaded');
      }
    };
    fetchTheme();
  }, []);

  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/dashboard/client');
    }
  }, [user, navigate]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put('/users/profile', {
        ...formData,
        onboarding_completed: true
      });
      toast.success('Bienvenue chez MSC L\'Agence !');
      navigate('/dashboard/client');
    } catch (error) {
      toast.success('Configuration terminée !');
      navigate('/dashboard/client');
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#1a365d] mb-2">Parlez-nous de votre entreprise</h2>
              <p className="text-[#64748b]">Ces informations nous aideront à personnaliser votre expérience</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de l'entreprise *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Votre entreprise"
                />
              </div>
              <div>
                <Label>Secteur d'activité</Label>
                <select
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sélectionner...</option>
                  <option value="tech">Tech / Startup</option>
                  <option value="retail">Commerce / Retail</option>
                  <option value="services">Services</option>
                  <option value="industry">Industrie</option>
                  <option value="health">Santé</option>
                  <option value="finance">Finance</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Taille de l'entreprise</Label>
                <select
                  value={formData.company_size}
                  onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sélectionner...</option>
                  <option value="1-10">1-10 employés</option>
                  <option value="11-50">11-50 employés</option>
                  <option value="51-200">51-200 employés</option>
                  <option value="200+">200+ employés</option>
                </select>
              </div>
              <div>
                <Label>Site web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Adresse de l'entreprise"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label>Ville</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#1a365d] mb-2">Quels sont vos objectifs ?</h2>
              <p className="text-[#64748b]">Nous pourrons mieux vous accompagner en comprenant vos besoins</p>
            </div>

            <div>
              <Label>Objectif principal</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  { id: 'brand', label: 'Créer/Renforcer mon image de marque', icon: Sparkles },
                  { id: 'digital', label: 'Développer ma présence digitale', icon: Globe },
                  { id: 'sales', label: 'Augmenter mes ventes', icon: TrendingUp },
                  { id: 'awareness', label: 'Gagner en notoriété', icon: Star }
                ].map(obj => (
                  <button
                    key={obj.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, main_objective: obj.id })}
                    className={cn(
                      "p-4 border-2 rounded-xl text-left transition-all flex items-center gap-3",
                      formData.main_objective === obj.id
                        ? "border-cyan-500 bg-cyan-50"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <obj.icon className={cn(
                      "w-6 h-6",
                      formData.main_objective === obj.id ? "text-cyan-500" : "text-slate-400"
                    )} />
                    <span className="font-medium">{obj.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Services qui vous intéressent</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Logo & Identité', 'Site Web', 'Vidéo', 'Print', 'Application', 'Marketing'].map(interest => (
                  <Badge
                    key={interest}
                    variant={formData.interests.includes(interest) ? 'default' : 'outline'}
                    className={cn(
                      "cursor-pointer px-4 py-2 text-sm",
                      formData.interests.includes(interest) && "bg-cyan-500"
                    )}
                    onClick={() => toggleInterest(interest)}
                  >
                    {formData.interests.includes(interest) && <Check className="w-3 h-3 mr-1" />}
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Budget estimé</Label>
                <select
                  value={formData.budget_range}
                  onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sélectionner...</option>
                  <option value="<2000">&lt; 2 000 €</option>
                  <option value="2000-5000">2 000 - 5 000 €</option>
                  <option value="5000-15000">5 000 - 15 000 €</option>
                  <option value=">15000">&gt; 15 000 €</option>
                </select>
              </div>
              <div>
                <Label>Délai souhaité</Label>
                <select
                  value={formData.project_timeline}
                  onChange={(e) => setFormData({ ...formData, project_timeline: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sélectionner...</option>
                  <option value="urgent">Urgent (&lt; 1 mois)</option>
                  <option value="short">Court terme (1-3 mois)</option>
                  <option value="medium">Moyen terme (3-6 mois)</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#1a365d] mb-2">Vos préférences de communication</h2>
              <p className="text-[#64748b]">Comment préférez-vous être contacté ?</p>
            </div>

            <div>
              <Label>Comment nous avez-vous connu ?</Label>
              <select
                value={formData.how_found_us}
                onChange={(e) => setFormData({ ...formData, how_found_us: e.target.value })}
                className="w-full border rounded-lg p-2"
              >
                <option value="">Sélectionner...</option>
                <option value="google">Google</option>
                <option value="social">Réseaux sociaux</option>
                <option value="referral">Recommandation</option>
                <option value="event">Événement</option>
                <option value="other">Autre</option>
              </select>
            </div>

            <div>
              <Label>Méthode de contact préférée</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: 'email', icon: Mail, label: 'Email' },
                  { id: 'phone', icon: Phone, label: 'Téléphone' },
                  { id: 'visio', icon: Users, label: 'Visio' }
                ].map(method => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, preferred_contact_method: method.id })}
                    className={cn(
                      "p-4 border-2 rounded-xl transition-all flex flex-col items-center gap-2",
                      formData.preferred_contact_method === method.id
                        ? "border-cyan-500 bg-cyan-50"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <method.icon className={cn(
                      "w-6 h-6",
                      formData.preferred_contact_method === method.id ? "text-cyan-500" : "text-slate-400"
                    )} />
                    <span className="text-sm font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={formData.newsletter}
                onChange={(e) => setFormData({ ...formData, newsletter: e.target.checked })}
                className="w-5 h-5"
              />
              <div>
                <p className="font-medium">S'abonner à la newsletter</p>
                <p className="text-sm text-slate-500">Recevez nos actualités et conseils créatifs</p>
              </div>
            </label>
          </div>
        );

      case 4:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1a365d] mb-4">Bienvenue chez MSC L'Agence !</h2>
            <p className="text-lg text-[#64748b] mb-8 max-w-md mx-auto">
              Votre espace client est prêt. Vous allez pouvoir découvrir tous nos services et suivre vos projets en temps réel.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
              {BENEFITS.map((benefit, i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-xl text-left">
                  <benefit.icon className="w-6 h-6 text-cyan-500 mb-2" />
                  <p className="font-medium text-[#1a365d]">{benefit.title}</p>
                  <p className="text-sm text-[#64748b]">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex">
      {/* Mobile Header - Logo & Return Link */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-[#0f172a]/90 backdrop-blur-lg border-b border-slate-700 z-50 px-4 py-3 flex items-center justify-between">
        <Link to="/site" className="flex items-center gap-2">
          {themeSettings?.logo?.lightUrl ? (
            <img 
              src={themeSettings.logo.lightUrl} 
              alt={themeSettings.agency?.name || "Logo"} 
              className="h-8 w-auto object-contain brightness-0 invert"
            />
          ) : (
            <>
              <div className="text-xl font-black">
                <span className="text-[#00d4aa]">M</span>
                <span className="text-white">SC</span>
              </div>
              <span className="text-white/70 text-sm">L&apos;Agence</span>
            </>
          )}
        </Link>
        <Link to="/site" className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-sm">
          <Home className="w-4 h-4" />
          <span>Retour</span>
        </Link>
      </div>

      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10">
          <Link to="/site" className="flex items-center gap-3 mb-6 hover:opacity-80 transition-opacity">
            {themeSettings?.logo?.lightUrl ? (
              <img 
                src={themeSettings.logo.lightUrl} 
                alt={themeSettings.agency?.name || "Logo"} 
                className="h-12 w-auto object-contain brightness-0 invert"
              />
            ) : (
              <>
                <div className="text-3xl font-black">
                  <span className="text-[#00d4aa]">M</span>
                  <span className="text-white">SC</span>
                </div>
                <span className="text-white/70 text-sm font-medium">L&apos;Agence</span>
              </>
            )}
          </Link>
          <Link 
            to="/site" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-12"
          >
            <Home className="w-4 h-4" />
            Retour au site
          </Link>

          <h1 className="text-4xl font-bold text-white mb-6">
            Bienvenue dans votre<br />
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              espace client
            </span>
          </h1>
          <p className="text-lg text-[#94a3b8] max-w-md">
            Configurez votre profil en quelques étapes pour bénéficier d&apos;une expérience personnalisée.
          </p>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
            ))}
            <span className="text-white font-semibold">4.9/5</span>
          </div>
          <p className="text-[#94a3b8]">
            &ldquo;Une équipe exceptionnelle qui a transformé notre image de marque.&rdquo;
          </p>
          <p className="text-white font-medium mt-2">- Marie D., CEO TechStart</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 pt-20 lg:pt-6">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                      currentStep > step.id
                        ? "bg-green-500 text-white"
                        : currentStep === step.id
                          ? "bg-cyan-500 text-white"
                          : "bg-slate-100 text-slate-400"
                    )}>
                      {currentStep > step.id ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={cn(
                      "text-xs mt-2 hidden sm:block",
                      currentStep === step.id ? "text-cyan-500 font-medium" : "text-slate-400"
                    )}>
                      {step.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={cn(
                      "flex-1 h-0.5 mx-2",
                      currentStep > step.id ? "bg-green-500" : "bg-slate-200"
                    )} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step Content */}
            {renderStep()}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                className={currentStep === 1 ? 'invisible' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>

              {currentStep < STEPS.length ? (
                <Button onClick={handleNext} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading} className="bg-gradient-to-r from-green-500 to-emerald-600">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                  Accéder à mon espace
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientOnboardingPage;
